import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import useDocumentTitle from "../../utils/useDocumentTitle";

import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import EditIcon from "@material-ui/icons/Edit";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import Button from "../../components/Button";

import PageContent from "../../components/PageContent";
import { Pagination } from "../../components/Pagination";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import { TableConfig } from "../../config/Config";
import { AdminRole, IsCampaignManager, Role } from "../../config/Roles";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import styles from "./Settings.module.scss";

import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { PerPage } from "../../components/PerPage";
import Search from "../../components/Search";
import { useCampaign } from "../../context/campaign";
import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";

dayjs.extend(utc);

export function SubjectManagement(props) {
  useDocumentTitle(props.title);
  const { t } = useTranslation();
  const history = useHistory();
  const { addNotification } = useNotification();
  const { selectedCampaign } = useCampaign();
  const [userData, setUserData] = useState(null);
  const [subjectList, setSubjectList] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [offset, setOffset] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage, setPerPage] = useState(TableConfig.resultPerPage);
  const [columnName, setColumnName] = useState("u_fullname");
  const [sortOrder, setSortOrder] = useState("asc");
  const [uid] = useState(getLocalValue("uid"));
  const [searchKey, setSearchKey] = useState("");

  const tableHeader = [
    {
      name: "department",
      label: t("subjectManagement.department"),
      width: "23%",
    },
    {
      name: "sub_functional_unit_name",
      label: t("subjectManagement.functionalUnit"),
      width: "23%",
    },
    {
      name: "scorecard_status",
      label: t("Scorecard Status"),
      width: "7%",
    },
    {
      name: "last_edit_user",
      label: t("subjectManagement.lastEdited"),
      width: "15%",
    },
    {
      name: "last_modified_datetime",
      label: t("subjectManagement.date"),
      width: "15%",
    },
    {
      name: "u_department",
      label: t("subjectManagement.subjectFor"),
      width: "10%",
    },

    { name: "u_status", label: t("subjectManagement.status"), width: "7%" },
  ];
  //ascending
  //descending

  // fetch from getSessionDetails api
  async function fetchData() {
    setIsLoading(true);
    const uid = getLocalValue("uid");
    const campaignParam = selectedCampaign
      ? `&campaign_id=${selectedCampaign}`
      : "";
    let u_coaching_role = parseInt(getLocalValue("u_coaching_role"));

    switch (u_coaching_role) {
      case Role.SUPER_ADMIN:
        u_coaching_role = AdminRole.SUPER_ADMIN;
        break;
      case Role.IT_MANAGER:
        u_coaching_role = AdminRole.IT_MANAGER;
        break;
      case Role.SUPPORT_ADMIN:
        u_coaching_role = AdminRole.SUPPORT_ADMIN;
        break;
      case Role.CAMPAIGN_MANAGER:
        u_coaching_role = AdminRole.CAMPAIGN_MANAGER;
        break;
      default:
        u_coaching_role = AdminRole.SUPPORT_ADMIN;
    }

    const roleParam = IsCampaignManager() ? `&role=${u_coaching_role}` : "";
    const response = await apiRequest.get(
      `/api/getSubjectManagementCampaignList?page=${offset}&limit=${perPage}&columnName=${columnName}&order=${sortOrder}&uid=${uid}&search=${searchKey}${campaignParam}${roleParam}`
    );

    if (response.status === 200) {
      const data = response.data;
      const subjects = response.data.data;
      setSubjectList(subjects);

      setTotalData(data.totalData);
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
    setIsLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, [offset, columnName, sortOrder, searchKey, selectedCampaign, perPage]);

  const handlePaginationChange = offset => {
    setOffset(offset.selected + 1);
  };

  const sortData = name => {
    setColumnName(name);
    const order = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(order);
  };

  const saveScorecardStatus = async campaign_id => {
    setIsLoading(true);
    let formData = {
      campaign_id: campaign_id,
      user_id: getLocalValue("uid"),
    };

    apiRequest
      .post(`/api/enableCampaignForScorecard`, formData)
      .then(resp => {
        console.log("Api response", resp);
        if (resp.status === 200 && resp.data.data.status) {
          const data = resp.data.data;
          setIsLoading(false);
          fetchData();
        } else {
          const errMsg = resp.data.data.message;
          if (errMsg && errMsg !== "") {
            addNotification(t(`${errMsg}`), "error");
          } else {
            addNotification(t("error.internalServerError"), "error");
          }

          setIsLoading(false);
        }
      })
      .catch(error => {
        addNotification(`${error}`, "error");
        setIsLoading(false);
      })
      .finally(() => { });
  };

  return (
    <DashboardLayout>
      <div className={styles.pageWrapper}>
        <PageContent>
          <Section>
            <SectionTitle>{t("leftNavigation.subjectManagement")}</SectionTitle>

            <Search
              handleSearch={val => {
                setSearchKey(val);
                setOffset(1);
              }}
            />

            <TableContainer component={Paper} elevation={0}>
              <Table aria-label="Team" className={styles.table}>
                <TableHead className={styles.tableHead}>
                  <TableRow>
                    {tableHeader.map((tableHead, index) => (
                      <TableCell
                        key={index}
                        style={{ width: tableHead.width }}
                        className={styles.tableHeadCell}
                        onClick={() => {
                          sortData(tableHead.name);
                        }}
                      >
                        {tableHead.label}

                        {columnName === tableHead.name &&
                          sortOrder === "asc" && (
                            <span>
                              <KeyboardArrowUpIcon fontSize="small" />
                            </span>
                          )}
                        {columnName === tableHead.name &&
                          sortOrder === "desc" && (
                            <span>
                              <KeyboardArrowDownIcon fontSize="small" />
                            </span>
                          )}
                      </TableCell>
                    ))}
                    <TableCell style={{ width: "3%" }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subjectList &&
                    subjectList.map((row, index) => {
                      // const status =
                      return (
                        <Fragment key={index}>
                          {row.subject_for &&
                            row.subject_for.map((childRow, childIndex) => {
                              const subjectForRow =
                                childIndex === 0 && row.subject_for.length > 1;
                              return (
                                <TableRow
                                  style={{
                                    borderBottom: `${subjectForRow
                                        ? "none"
                                        : "1px solid #eeeeee"
                                      }`,
                                  }}
                                  key={index + "" + childIndex}
                                  className={styles.tableRow}
                                  onClick={() => {
                                    history.push(
                                      `/settings/subjectManagement/editSubject/${row.campaign_id
                                      }?subjectFor=${childRow.subject_for
                                        ? childRow.subject_for === "Team Lead"
                                          ? 1
                                          : 2
                                        : 2
                                      }`
                                    );
                                  }}
                                >
                                  <TableCell
                                    style={{
                                      borderBottom: `${subjectForRow
                                          ? "none"
                                          : "1px solid #eeeeee"
                                        }`,
                                    }}
                                  >
                                    {childIndex === 0 && row.department}
                                  </TableCell>
                                  <TableCell
                                    style={{
                                      borderBottom: `${subjectForRow
                                          ? "none"
                                          : "1px solid #eeeeee"
                                        }`,
                                    }}
                                  >
                                    {childIndex === 0 &&
                                      `${row.sub_functional_unit}`}
                                  </TableCell>
                                  <TableCell
                                    style={{
                                      borderBottom: `${subjectForRow
                                          ? "none"
                                          : "1px solid #eeeeee"
                                        }`,
                                    }}
                                  >
                                    {childIndex === 0 ? (
                                      row.scorecard_status === 0 ? (
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          fullWidth
                                          style={{
                                            border: "none",
                                            height: "25px",
                                            fontSize: "14px",
                                          }}
                                          onClick={e => {
                                            saveScorecardStatus(
                                              row.campaign_id
                                            );
                                            e.stopPropagation();
                                          }}
                                        >
                                          {t("Enable")}
                                        </Button>
                                      ) : (
                                        <span className={styles.statusActive}>
                                          Active
                                        </span>
                                      )
                                    ) : (
                                      ""
                                    )}
                                  </TableCell>
                                  <TableCell
                                    style={{
                                      borderBottom: `${subjectForRow
                                          ? "none"
                                          : "1px solid #eeeeee"
                                        }`,
                                    }}
                                  >
                                    {" "}
                                    {childRow.last_edit_user}
                                  </TableCell>
                                  <TableCell
                                    style={{
                                      borderBottom: `${subjectForRow
                                          ? "none"
                                          : "1px solid #eeeeee"
                                        }`,
                                    }}
                                  >
                                    {childRow.last_modified_datetime
                                      ? dayjs(
                                        childRow.last_modified_datetime
                                      ).format("DD MMM YYYY")
                                      : ""}
                                  </TableCell>
                                  <TableCell
                                    style={{
                                      borderBottom: `${subjectForRow
                                          ? "none"
                                          : "1px solid #eeeeee"
                                        }`,
                                    }}
                                  >
                                    {childRow.subject_for}
                                  </TableCell>
                                  <TableCell
                                    style={{
                                      borderBottom: `${subjectForRow
                                          ? "none"
                                          : "1px solid #eeeeee"
                                        }`,
                                    }}
                                    className={
                                      childRow.status === 0
                                        ? styles.statusInactive
                                        : childRow.status === 1
                                          ? styles.statusActive
                                          : styles.statusNew
                                    }
                                  >
                                    {childRow.status === 0
                                      ? "Inactive"
                                      : childRow.status === 1
                                        ? "Active"
                                        : "New"}
                                  </TableCell>
                                  <TableCell
                                    style={{
                                      borderBottom: `${subjectForRow
                                          ? "none"
                                          : "1px solid #eeeeee"
                                        }`,
                                    }}
                                  >
                                    <IconButton
                                      className={styles.editIcon}
                                      size="small"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </Fragment>
                      );
                    })}
                  {_.isEmpty(subjectList) && (
                    <TableRow style={{ borderBottom: "1px solid #eeeeee" }}>
                      <TableCell style={{ borderBottom: 0 }} colSpan={9}>
                        {t("common.noRecordFound")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {totalData > perPage && (
              <div className={styles.footer}>
                <PerPage limit={perPage} setLimit={setPerPage} />
                <Pagination
                  totalData={totalData}
                  limit={perPage}
                  offset={offset}
                  handlePageClick={offset => {
                    handlePaginationChange(offset);
                  }}
                />
              </div>
            )}
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
